// ============================================================================
// COMMERCIAL (Party + Sales masters) HOOKS — server-side list hooks.
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { partyService, serviceCategoryService, serviceService, salesInvoiceService, creditNoteService, purchaseInvoiceService, purchaseDebitNoteService, supplierSetoffService, customerSetoffService, customerGroupService } from 'api/services';
import type {
  PaginationMeta,
  PartyDto, PartyQueryParams,
  PartyGroupDto, PartyGroupQueryParams,
  ServiceCategoryDto, ServiceCategoryQueryParams,
  ServiceDto, ServiceQueryParams,
  SalesInvoiceDto, SalesInvoiceQueryParams,
  CreditNoteDto, CreditNoteQueryParams, SalesNoteRegisterRowDto, SalesNoteRegisterQueryParams,
  PurchaseNoteRegisterRowDto, PurchaseNoteRegisterQueryParams,
  PurchaseInvoiceDto, PurchaseInvoiceQueryParams,
  PurchaseDebitNoteDto, PurchaseDebitNoteQueryParams,
  SetoffDto, SetoffQueryParams,
  DocumentLineLedgerDto, ServiceLineLedgerDto,
} from 'api/types';

const DEFAULT_PAGINATION: PaginationMeta = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

function useList<TDto, TParams>(
  fetcher: (params?: TParams) => Promise<{ data: TDto[]; pagination: PaginationMeta }>,
  params?: TParams,
) {
  const [data, setData] = useState<TDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>(DEFAULT_PAGINATION);
  const paramsKey = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await fetcher(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) { setError(err as Error); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, error, pagination, refetch: fetchData };
}

export const useParties = (params?: PartyQueryParams) =>
  useList<PartyDto, PartyQueryParams>((p) => partyService.getAll(p), params);

export const useServiceCategories = (params?: ServiceCategoryQueryParams) =>
  useList<ServiceCategoryDto, ServiceCategoryQueryParams>((p) => serviceCategoryService.getAll(p), params);

export const useCustomerGroups = (params?: PartyGroupQueryParams) =>
  useList<PartyGroupDto, PartyGroupQueryParams>((p) => customerGroupService.getAll(p), params);

export const useServices = (params?: ServiceQueryParams) =>
  useList<ServiceDto, ServiceQueryParams>((p) => serviceService.getAll(p), params);

export const useSalesInvoices = (params?: SalesInvoiceQueryParams) =>
  useList<SalesInvoiceDto, SalesInvoiceQueryParams>((p) => salesInvoiceService.getAll(p), params);

export const useCreditNotes = (params?: CreditNoteQueryParams) =>
  useList<CreditNoteDto, CreditNoteQueryParams>((p) => creditNoteService.getAll(p), params);

/** Credit notes + Sales Debit Notes in one register (ADR-0053). */
export const useSalesNotesRegister = (params?: SalesNoteRegisterQueryParams) =>
  useList<SalesNoteRegisterRowDto, SalesNoteRegisterQueryParams>((p) => creditNoteService.getRegister(p), params);

export const usePurchaseInvoices = (params?: PurchaseInvoiceQueryParams) =>
  useList<PurchaseInvoiceDto, PurchaseInvoiceQueryParams>((p) => purchaseInvoiceService.getAll(p), params);

/** Our debit notes + Supplier Debit Notes in one register (ADR-0053). */
export const usePurchaseNotesRegister = (params?: PurchaseNoteRegisterQueryParams) =>
  useList<PurchaseNoteRegisterRowDto, PurchaseNoteRegisterQueryParams>((p) => purchaseDebitNoteService.getRegister(p), params);

export const usePurchaseDebitNotes = (params?: PurchaseDebitNoteQueryParams) =>
  useList<PurchaseDebitNoteDto, PurchaseDebitNoteQueryParams>((p) => purchaseDebitNoteService.getAll(p), params);

export const useSupplierSetoffs = (params?: SetoffQueryParams) =>
  useList<SetoffDto, SetoffQueryParams>((p) => supplierSetoffService.getAll(p), params);

export const useCustomerSetoffs = (params?: SetoffQueryParams) =>
  useList<SetoffDto, SetoffQueryParams>((p) => customerSetoffService.getAll(p), params);

/** The document whose line-ledgers read a hook asks (each has its own endpoint). */
type LineLedgerDocument = 'PURCHASE_INVOICE' | 'PURCHASE_DEBIT_NOTE' | 'SALES_INVOICE' | 'CREDIT_NOTE';
const fetchLineLedgers = (doc: LineLedgerDocument, date: string, itemIds: string[]): Promise<DocumentLineLedgerDto[]> =>
  doc === 'PURCHASE_DEBIT_NOTE' ? purchaseDebitNoteService.getLineLedgers(date, itemIds)
  : doc === 'SALES_INVOICE' ? salesInvoiceService.getLineLedgers(date, itemIds)
  : doc === 'CREDIT_NOTE' ? creditNoteService.getLineLedgers(date, itemIds)
  : purchaseInvoiceService.getLineLedgers(date, itemIds);

/**
 * The ledger each item on a document posts to on its date, keyed by item id. Refetches when the
 * item set or the date changes; a stale answer (the set moved on meanwhile) is dropped.
 * Best-effort — a failed read leaves the map empty.
 */
const useLineLedgers = (
  document: LineLedgerDocument, date: string, itemIds: string[], enabled: boolean,
): Record<string, DocumentLineLedgerDto> => {
  const [byItem, setByItem] = useState<Record<string, DocumentLineLedgerDto>>({});
  const ids = Array.from(new Set(itemIds.filter(Boolean))).sort();
  const key = enabled && date && ids.length ? `${document}|${date}|${ids.join(',')}` : '';
  useEffect(() => {
    if (!key) { setByItem({}); return; }
    let active = true;
    const [doc, d, list] = key.split('|');
    fetchLineLedgers(doc as LineLedgerDocument, d, list.split(','))
      .then(rows => { if (active) setByItem(Object.fromEntries(rows.map(r => [r.itemId, r]))); })
      .catch(() => { if (active) setByItem({}); });
    return () => { active = false; };
  }, [key]);
  return byItem;
};

/** The ledger each item on a purchase document posts to (bill: booking date; debit note: note date). */
export const usePurchaseLineLedgers = (
  document: 'INVOICE' | 'DEBIT_NOTE', date: string, itemIds: string[], enabled = true,
): Record<string, DocumentLineLedgerDto> =>
  useLineLedgers(document === 'DEBIT_NOTE' ? 'PURCHASE_DEBIT_NOTE' : 'PURCHASE_INVOICE', date, itemIds, enabled);

/** The income ledger each item on a sales document posts to (invoice date / credit note date). */
export const useSalesLineLedgers = (
  document: 'INVOICE' | 'CREDIT_NOTE', date: string, itemIds: string[], enabled = true,
): Record<string, DocumentLineLedgerDto> =>
  useLineLedgers(document === 'CREDIT_NOTE' ? 'CREDIT_NOTE' : 'SALES_INVOICE', date, itemIds, enabled);

/**
 * The income ledger each picked service on a sales document posts to (invoice / debit note) or
 * reverses (credit note), keyed by service id. Refetches when the service set changes; a stale
 * answer is dropped. Best-effort — a failed read leaves the map empty.
 */
export const useSalesServiceLedgers = (
  document: 'INVOICE' | 'CREDIT_NOTE', serviceIds: string[], enabled = true,
): Record<string, ServiceLineLedgerDto> => {
  const [byService, setByService] = useState<Record<string, ServiceLineLedgerDto>>({});
  const ids = Array.from(new Set(serviceIds.filter(Boolean))).sort();
  const key = enabled && ids.length ? `${document}|${ids.join(',')}` : '';
  useEffect(() => {
    if (!key) { setByService({}); return; }
    let active = true;
    const [doc, list] = key.split('|');
    const idList = list.split(',');
    (doc === 'CREDIT_NOTE' ? creditNoteService.getServiceLedgers(idList) : salesInvoiceService.getServiceLedgers(idList))
      .then(rows => { if (active) setByService(Object.fromEntries(rows.map(r => [r.serviceId, r]))); })
      .catch(() => { if (active) setByService({}); });
    return () => { active = false; };
  }, [key]);
  return byService;
};

/** Key of one on-hand probe in the map useSalesStockOnHand returns. */
export const stockOnHandKey = (itemId: string, stockLocationId: string) => `${itemId}|${stockLocationId}`;

/**
 * Base-unit quantity on hand for each (item, location) pair on the date — the sales item line's
 * hint. Reads are debounced and cached per item/location/date, so typing a quantity never
 * refetches; a failed read just leaves that pair out. Keyed by stockOnHandKey.
 */
export const useSalesStockOnHand = (
  pairs: { itemId: string; stockLocationId: string }[], date: string, enabled = true,
): Record<string, number> => {
  const cache = useRef<Map<string, number>>(new Map());
  const [, setVersion] = useState(0);
  const wanted = Array.from(new Set(pairs.filter(p => p.itemId && p.stockLocationId)
    .map(p => stockOnHandKey(p.itemId, p.stockLocationId)))).sort();
  const key = enabled && date && wanted.length ? `${date}#${wanted.join(',')}` : '';
  // A closed form forgets its readings — the next document sees stock after this one posted.
  useEffect(() => { if (!enabled) cache.current.clear(); }, [enabled]);
  useEffect(() => {
    if (!key) return;
    const [d, list] = key.split('#');
    const missing = list.split(',').filter(k => !cache.current.has(`${k}|${d}`));
    if (!missing.length) return;
    let active = true;
    const timer = setTimeout(() => {
      Promise.all(missing.map(k => {
        const [itemId, locId] = k.split('|');
        return salesInvoiceService.getStockOnHand(itemId, locId, d)
          .then(q => { cache.current.set(`${k}|${d}`, q); })
          .catch(() => { /* no hint for this pair */ });
      })).then(() => { if (active) setVersion(v => v + 1); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [key]);
  const out: Record<string, number> = {};
  if (enabled && date) {
    wanted.forEach(k => {
      const q = cache.current.get(`${k}|${date}`);
      if (q !== undefined) out[k] = q;
    });
  }
  return out;
};
